import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import InputSelect from "../../../components/InputSelect";
import { getWorkflowHooks, saveWorkflowHook, workflowHookUrl } from "../../../api";
import { WorkflowHook } from "../types";

/** Chooses an incoming webhook, or creates one on the spot. */
const HookSelect = ({ value, onChange, invalid }: { value: string | null; onChange: (uuid: string | null) => void; invalid?: boolean }) => {
    const { t } = useTranslation(['workflow']);
    const [hooks, setHooks] = useState<WorkflowHook[]>([]);
    const [creating, setCreating] = useState(false);
    const [name, setName] = useState('');

    const load = useCallback(() => {
        getWorkflowHooks().then((r: any) => setHooks(r?.items || [])).catch(() => setHooks([]));
    }, []);

    useEffect(() => load(), [load]);

    const selected = hooks.find((h) => h.uuid === value);

    const create = () => {
        if (!name.trim()) {
            return;
        }
        saveWorkflowHook({ name: name.trim() })
            .then((r: any) => {
                setCreating(false);
                setName('');
                load();
                onChange(r.uuid);
            })
            .catch((e: any) => toast.error(e?.message || t('workflow:saveFailed')));
    };

    return (
        <div>
            <div className="d-flex gap-2 align-items-start">
                <div className="flex-grow-1 mw-0">
                    <InputSelect
                        items={hooks.map((h) => ({ uuid: h.uuid, name: h.name + (h.scenario ? ` (${h.scenario.name})` : '') }))}
                        value={value || null}
                        placeholder={t('workflow:chooseWebhook')}
                        onChange={(option: any) => onChange(option ? option.value : null)}
                        isClearable
                        required={false}
                        readonly={false}
                        isLoading={false}
                        className={invalid ? 'is-invalid z-index-auto zindex-3' : undefined}
                    />
                </div>
                <button type="button" className="btn btn-outline-primary btn-icon flex-shrink-0" title={t('workflow:add')} onClick={() => setCreating(!creating)}>
                    <i className="ri-add-line" />
                </button>
            </div>
            {creating &&
                <div className="d-flex gap-2 mt-2">
                    <input className="form-control" placeholder={t('workflow:webhookName')} value={name} onChange={(e) => setName(e.target.value)} />
                    <button type="button" className="btn btn-primary" onClick={create}>{t('workflow:create')}</button>
                </div>
            }
            {selected &&
                <div className="input-group mt-2">
                    <input className="form-control font-monospace" readOnly value={workflowHookUrl(selected.path)} />
                    <button type="button" className="btn btn-soft-secondary" title={t('common:copy')} onClick={() => navigator.clipboard?.writeText(workflowHookUrl(selected.path)).then(() => toast.success(t('workflow:copied')))}>
                        <i className="ri-file-copy-line" />
                    </button>
                </div>
            }
        </div>
    );
};

export default HookSelect;
